import { useState, useRef } from "react";
import { useCondominio, useCuentas } from "../hooks/useSettings";
import { formatCurrency } from "../../../shared/utils/formatCurrency";
import { Link } from "react-router-dom";
import apiFetch from "../../../shared/services/apiClient";
import { useMultimoneda, setMultimonedaFlag } from "../../../shared/hooks/useMultimoneda";

/**
 * compressImage — reduce el tamaño de un archivo de imagen antes de subirlo.
 * - PNG: se redimensiona manteniendo transparencia.
 * - JPG/WebP/etc: se convierte a JPEG con calidad reducida.
 * - SVG o archivos < 200 KB: se devuelven sin cambios.
 * @param {File}   file
 * @param {number} maxWidth  px máximos de ancho (default 1200)
 * @param {number} maxHeight px máximos de alto  (default 900)
 * @returns {Promise<File>}
 */
async function compressImage(file, maxWidth = 1200, maxHeight = 900) {
  return new Promise((resolve) => {
    // SVG y archivos pequeños no necesitan compresión
    if (!file || file.type === "image/svg+xml" || file.size < 200 * 1024) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement("canvas");
        canvas.width  = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        const keepPng = file.type === "image/png"; // preservar transparencia
        if (!keepPng) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        const mimeOut = keepPng ? "image/png" : "image/jpeg";
        const quality = keepPng ? undefined : 0.85;

        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) { resolve(file); return; }
            const ext     = keepPng ? "png" : "jpg";
            const newName = file.name.replace(/\.[^.]+$/, `.${ext}`);
            resolve(new File([blob], newName, { type: mimeOut, lastModified: Date.now() }));
          },
          mimeOut,
          quality,
        );
      };
      img.onerror = () => resolve(file); // si falla, usar original
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

const C = {
  bg0: "rgba(255,255,255,0.03)", bg1: "rgba(255,255,255,0.05)",
  bg2: "rgba(255,255,255,0.05)", bg3: "rgba(255,255,255,0.08)",
  border: "rgba(255,255,255,0.07)", accent: "#f97316",
  text: "#f1f5f9", muted: "#64748b", faint: "#334155",
};

function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:"0.7rem", fontWeight:700,
      letterSpacing:"0.7px", textTransform:"uppercase", marginBottom:6, color:C.muted }}>
      {children}{required && <span style={{ color:"#f87171", marginLeft:3 }}>*</span>}
    </label>
  );
}

function Input({ style={}, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <input {...props}
      onFocus={e=>{setFocus(true);props.onFocus?.(e);}}
      onBlur={e=>{setFocus(false);props.onBlur?.(e);}}
      style={{ width:"100%", background:"rgba(255,255,255,0.06)", borderRadius:8,
        border:`1px solid ${focus?C.accent:C.border}`,
        padding:"10px 14px", color:"#f1f5f9", fontSize:"0.88rem",
        outline:"none", boxSizing:"border-box",
        boxShadow: focus?"0 0 0 3px rgba(249,115,22,0.08)":"none",
        transition:"border-color 0.15s", ...style }}/>
  );
}

function Btn({ children, onClick, variant="primary", disabled, small }) {
  const [h, setH] = useState(false);
  const cfg = {
    primary: { bg:h?"#ea6b10":"#f97316", color:"#fff", border:"#f97316" },
    ghost:   { bg:h?"rgba(255,255,255,0.05)":"transparent", color:C.muted, border:C.border },
    danger:  { bg:h?"rgba(239,68,68,0.18)":"rgba(239,68,68,0.08)", color:"#f87171", border:"rgba(239,68,68,0.3)" },
  }[variant]??{};
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:8,
        color:cfg.color, padding:small?"5px 12px":"9px 18px",
        fontSize:small?"0.78rem":"0.85rem", fontWeight:600,
        cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1,
        transition:"all 0.15s", display:"inline-flex", alignItems:"center",
        gap:7, whiteSpace:"nowrap", fontFamily:"'Inter',sans-serif" }}>
      {children}
    </button>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const ok = type !== "error";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10,
      background:ok?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",
      border:`1px solid ${ok?"rgba(16,185,129,0.25)":"rgba(239,68,68,0.25)"}`,
      borderLeft:`3px solid ${ok?"#10b981":"#ef4444"}`,
      borderRadius:8, padding:"10px 14px", marginBottom:16,
      color:ok?"#6ee7b7":"#fca5a5", fontSize:"0.84rem", fontWeight:500 }}>
      <i className={`fa-solid ${ok?"fa-circle-check":"fa-circle-exclamation"}`} style={{flexShrink:0}}/>
      {msg}
    </div>
  );
}

function ImageCard({ label, icon, hint, currentUrl, onFile, onClear, bgLight=false }) {
  const ref = useRef();
  return (
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:10, padding:18 }}>
      <p style={{ margin:"0 0 14px", fontWeight:600, color:C.text,
        fontSize:"0.85rem", display:"flex", alignItems:"center", gap:8 }}>
        <i className={`fa-solid ${icon}`} style={{color:C.accent,fontSize:"0.8rem"}}/>
        {label}
      </p>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div onClick={()=>ref.current.click()}
          style={{ width:68, height:68, borderRadius:10, flexShrink:0,
            border:`2px dashed ${C.faint}`, background:bgLight?"#f8fafc":C.bg0,
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", overflow:"hidden", transition:"border-color 0.15s" }}
          onMouseOver={e=>e.currentTarget.style.borderColor=C.accent}
          onMouseOut={e=>e.currentTarget.style.borderColor=C.faint}>
          {currentUrl
            ? <img src={currentUrl} alt={label}
                style={{width:"100%",height:"100%",objectFit:"contain",padding:bgLight?4:0}}/>
            : <i className={`fa-solid ${icon}`}
                 style={{fontSize:"1.4rem",color:bgLight?"#94a3b8":C.faint}}/>}
        </div>
        <div>
          <p style={{margin:"0 0 4px",fontSize:"0.76rem",color:C.muted}}>{hint}</p>
          {currentUrl && (
            <p style={{margin:"0 0 6px",fontSize:"0.72rem",color:"#10b981",
              display:"flex",alignItems:"center",gap:4}}>
              <i className="fa-solid fa-circle-check"/>Imagen cargada
            </p>
          )}
          <div style={{display:"flex",gap:6}}>
            <Btn small onClick={()=>ref.current.click()}>
              <i className="fa-solid fa-upload"/>{currentUrl?"Cambiar":"Subir"}
            </Btn>
            {currentUrl && (
              <Btn small variant="danger" onClick={onClear}>
                <i className="fa-solid fa-trash"/>Quitar
              </Btn>
            )}
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" style={{display:"none"}}
        onChange={e=>{const f=e.target.files[0];if(f)onFile(f);}}/>
    </div>
  );
}

function CondominioSection() {
  const { data, loading, update, clearImage, refetch } = useCondominio();
  const [form,         setForm]        = useState(null);
  const [logoPreview,  setLogoPreview]  = useState(null);
  const [firmaPreview, setFirmaPreview] = useState(null);
  const [reprPreview,  setReprPreview]  = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState({msg:"",type:""});
  const [togglingMM,   setTogglingMM]   = useState(false);
  // null = cerrado | { newVal: bool } = confirmación pendiente
  const [mmConfirm,    setMmConfirm]    = useState(null);

  if (data && !form) {
    setForm({
      nombre:          data.nombre          ?? "",
      cedulaJuridica:  data.cedulaJuridica  ?? "",
      email:           data.email           ?? "",
      direccion:       data.direccion       ?? "",
      nombre_contador: data.nombre_contador ?? "",
    });
  }

  const field = key => ({ value:form?.[key]??"", onChange:e=>setForm(f=>({...f,[key]:e.target.value})) });

  async function handleSave() {
    setSaving(true); setToast({msg:"",type:""});
    try {
      // Comprimir imágenes en paralelo antes de enviar (evita error 413)
      // Logo: max 1200×900 px | Firmas: max 800×400 px (más estrechas)
      const [logoFile, firmaFile, reprFile] = await Promise.all([
        form._logoFile  ? compressImage(form._logoFile,  1200, 900) : Promise.resolve(null),
        form._firmaFile ? compressImage(form._firmaFile,  800, 400) : Promise.resolve(null),
        form._reprFile  ? compressImage(form._reprFile,   800, 400) : Promise.resolve(null),
      ]);

      const fd = new FormData();
      fd.append("nombre",          form.nombre);
      fd.append("cedulaJuridica",  form.cedulaJuridica);
      fd.append("email",           form.email);
      fd.append("direccion",       form.direccion);
      fd.append("nombre_contador", form.nombre_contador??"");
      if (logoFile)  fd.append("logo",                logoFile);
      if (firmaFile) fd.append("firma_contador",      firmaFile);
      if (reprFile)  fd.append("firma_representante", reprFile);

      await update(fd);
      setForm(f=>({...f,_logoFile:null,_firmaFile:null,_reprFile:null}));
      setToast({msg:"Información actualizada correctamente",type:"success"});
    } catch(e) { setToast({msg:e.message,type:"error"}); }
    finally { setSaving(false); }
  }

  // Abre el panel de confirmación en lugar de window.confirm
  function handleToggleMultimoneda() {
    if (!data || togglingMM) return;
    const newVal = !data.multimoneda_activa;
    setMmConfirm({ newVal });
  }

  // Ejecuta el toggle tras confirmar en el panel
  async function confirmToggleMultimoneda() {
    if (!mmConfirm) return;
    const { newVal } = mmConfirm;
    setMmConfirm(null);
    setTogglingMM(true);
    try {
      const res = await apiFetch(`/api/residents/condominio/${data.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ multimoneda_activa: newVal }),
      });
      if (!res.ok) throw new Error("Error al guardar");
      setMultimonedaFlag(newVal);
      await refetch();
      setToast({ msg: `Multimoneda ${newVal ? "activada ✓" : "desactivada"}`, type: "success" });
    } catch(e) {
      setToast({ msg: e.message, type: "error" });
    } finally {
      setTogglingMM(false);
    }
  }

  async function handleClear(fieldName) {
    try {
      await clearImage(fieldName);
      if (fieldName==='logo')                { setLogoPreview(null);  setForm(f=>({...f,_logoFile:null})); }
      if (fieldName==='firma_contador')      { setFirmaPreview(null); setForm(f=>({...f,_firmaFile:null})); }
      if (fieldName==='firma_representante') { setReprPreview(null);  setForm(f=>({...f,_reprFile:null})); }
      setToast({msg:"Imagen eliminada",type:"success"});
    } catch(e) { setToast({msg:e.message,type:"error"}); }
  }

  if (loading) return (
    <div style={{padding:40,textAlign:"center",color:C.muted}}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{color:C.accent,marginRight:8}}/>Cargando…
    </div>
  );

  const logoUrl  = logoPreview  ?? data?.logo_url                ?? null;
  const firmaUrl = firmaPreview ?? data?.firma_url               ?? null;
  const reprUrl  = reprPreview  ?? data?.firma_representante_url ?? null;

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))",gap:12,marginBottom:20}}>
        <ImageCard label="Logo del condominio" icon="fa-image"
          hint="PNG, JPG o SVG. Aparece en PDFs y correos."
          currentUrl={logoUrl}
          onFile={f=>{setLogoPreview(URL.createObjectURL(f));setForm(p=>({...p,_logoFile:f}));}}
          onClear={()=>handleClear('logo')} bgLight={false}/>
        <ImageCard label="Firma del Contador" icon="fa-signature"
          hint="PNG fondo transparente. Para facturas y recibos."
          currentUrl={firmaUrl}
          onFile={f=>{setFirmaPreview(URL.createObjectURL(f));setForm(p=>({...p,_firmaFile:f}));}}
          onClear={()=>handleClear('firma_contador')} bgLight={true}/>
        <ImageCard label="Firma del Representante Legal" icon="fa-pen-nib"
          hint="PNG fondo transparente. Para estados financieros."
          currentUrl={reprUrl}
          onFile={f=>{setReprPreview(URL.createObjectURL(f));setForm(p=>({...p,_reprFile:f}));}}
          onClear={()=>handleClear('firma_representante')} bgLight={true}/>
      </div>

      {/* ── Multimoneda toggle ─────────────────────────────────────────── */}
      {(() => {
        const mmActiva = !!data?.multimoneda_activa;
        return (
          <div style={{ marginBottom: 16 }}>
            {/* Fila principal */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: mmActiva ? "rgba(96,165,250,0.06)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${mmActiva ? "rgba(96,165,250,0.22)" : C.border}`,
              borderRadius: mmConfirm ? "10px 10px 0 0" : 10,
              padding: "14px 18px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                  background: mmActiva ? "rgba(96,165,250,0.15)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${mmActiva ? "rgba(96,165,250,0.3)" : C.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className="fa-solid fa-dollar-sign"
                     style={{ color: mmActiva ? "#60a5fa" : C.muted, fontSize: "0.9rem" }}/>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: C.text, fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 8 }}>
                    Soporte Multimoneda (USD)
                    {mmActiva && (
                      <span style={{
                        fontSize: "0.62rem", padding: "1px 7px", borderRadius: 4,
                        background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontWeight: 700, letterSpacing: "0.4px",
                      }}>ACTIVO</span>
                    )}
                  </div>
                  <div style={{ color: C.muted, fontSize: "0.73rem", marginTop: 2, lineHeight: 1.4 }}>
                    {mmActiva
                      ? "Puede cobrar facturas y recibir pagos en dólares (USD)"
                      : "Activar para cobrar facturas y recibir pagos en dólares (USD)"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleToggleMultimoneda}
                disabled={togglingMM || !data || !!mmConfirm}
                style={{
                  padding: "8px 18px", borderRadius: 8, fontWeight: 700,
                  fontSize: "0.82rem", cursor: (togglingMM || mmConfirm) ? "not-allowed" : "pointer",
                  opacity: (togglingMM || mmConfirm) ? 0.5 : 1, transition: "all 0.15s",
                  border: `1px solid ${mmActiva ? "rgba(239,68,68,0.35)" : "rgba(96,165,250,0.35)"}`,
                  background: mmActiva ? "rgba(239,68,68,0.08)" : "rgba(96,165,250,0.1)",
                  color: mmActiva ? "#f87171" : "#60a5fa",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {togglingMM
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</>
                  : mmActiva
                    ? <><i className="fa-solid fa-toggle-on"/>Desactivar</>
                    : <><i className="fa-solid fa-toggle-off"/>Activar</>}
              </button>
            </div>

            {/* Panel de confirmación inline — reemplaza window.confirm */}
            {mmConfirm && (
              <div style={{
                background: mmConfirm.newVal
                  ? "rgba(96,165,250,0.07)"
                  : "rgba(239,68,68,0.07)",
                border: `1px solid ${mmConfirm.newVal ? "rgba(96,165,250,0.3)" : "rgba(239,68,68,0.3)"}`,
                borderTop: "none",
                borderRadius: "0 0 10px 10px",
                padding: "16px 18px",
              }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 14 }}>
                  <i className={`fa-solid ${mmConfirm.newVal ? "fa-circle-info" : "fa-triangle-exclamation"}`}
                     style={{
                       color: mmConfirm.newVal ? "#60a5fa" : "#f87171",
                       fontSize: "1.05rem", marginTop: 2, flexShrink: 0,
                     }}/>
                  <div style={{ fontSize: "0.84rem", color: C.text, lineHeight: 1.55 }}>
                    {mmConfirm.newVal ? (
                      <>
                        <strong style={{ color: "#60a5fa" }}>¿Activar soporte multimoneda (CRC + USD)?</strong><br/>
                        Podrá cobrar facturas y recibir pagos en dólares.<br/>
                        Si ya tiene saldos en dólares, deberá hacer un asiento de ajuste manual.
                      </>
                    ) : (
                      <>
                        <strong style={{ color: "#f87171" }}>¿Desactivar multimoneda?</strong><br/>
                        Los campos USD quedarán ocultos y los bancos en dólares dejarán de aparecer
                        en los formularios de recibos y depósitos.<br/>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          marginTop: 6, background: "rgba(239,68,68,0.12)",
                          border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6,
                          padding: "5px 10px", color: "#fca5a5", fontSize: "0.81rem",
                        }}>
                          <i className="fa-solid fa-building-columns"/>
                          Si tiene cuentas en dólares, las mismas van a quedar desactivadas —
                          revisá antes de confirmar.
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => setMmConfirm(null)}
                    style={{
                      padding: "7px 16px", borderRadius: 7, fontSize: "0.82rem",
                      fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${C.border}`,
                      background: "transparent", color: C.muted,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmToggleMultimoneda}
                    style={{
                      padding: "7px 18px", borderRadius: 7, fontSize: "0.82rem",
                      fontWeight: 700, cursor: "pointer",
                      border: `1px solid ${mmConfirm.newVal ? "rgba(96,165,250,0.4)" : "rgba(239,68,68,0.4)"}`,
                      background: mmConfirm.newVal ? "rgba(96,165,250,0.15)" : "rgba(239,68,68,0.15)",
                      color: mmConfirm.newVal ? "#60a5fa" : "#f87171",
                    }}
                  >
                    {mmConfirm.newVal ? "Sí, activar" : "Sí, desactivar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))",gap:14,marginBottom:20}}>
        <div><Label required>Nombre del condominio</Label>
          <Input {...field("nombre")} placeholder="Ej: Residencial Los Pinos"/></div>
        <div><Label required>Cédula Jurídica</Label>
          <Input {...field("cedulaJuridica")} placeholder="3-102-000000"/></div>
        <div><Label required>Email</Label>
          <Input {...field("email")} type="email" placeholder="admin@condominio.com"/></div>
        <div><Label>Dirección</Label>
          <Input {...field("direccion")} placeholder="Dirección del condominio"/></div>
        <div style={{gridColumn:"1 / -1"}}><Label>Nombre del Contador (para firmas en PDFs)</Label>
          <Input {...field("nombre_contador")} placeholder="Ej: Juan Pérez Rodríguez, CPA"/></div>
      </div>

      <div style={{display:"flex",justifyContent:"flex-end",paddingTop:16,borderTop:`1px solid ${C.border}`}}>
        <Btn onClick={handleSave} disabled={saving}>
          {saving?<><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</>
                 :<><i className="fa-solid fa-floppy-disk"/>Guardar cambios</>}
        </Btn>
      </div>
    </div>
  );
}

const MONEDAS = ["CRC","USD","EUR"];
const EMPTY_CUENTA = {banco:"",numero_cuenta:"",iban:"",moneda:"CRC",saldo:"0"};
const MONEDA_COLOR  = {CRC:"#f97316",USD:"#4ade80",EUR:"#60a5fa"};
const MONEDA_SYMBOL = {CRC:"₡",USD:"$",EUR:"€"};

function CuentasSection() {
  const { cuentas, loading, create, remove, update } = useCuentas();
  const multimoneda = useMultimoneda();
  const [form,setForm]=useState(EMPTY_CUENTA);
  const [editId,setEditId]=useState(null);
  const [editForm,setEditForm]=useState(EMPTY_CUENTA);
  const [adding,setAdding]=useState(false);
  const [toast,setToast]=useState({msg:"",type:""});
  const [usdBlockAlert,setUsdBlockAlert]=useState(false);
  const [usdEditBlockAlert,setUsdEditBlockAlert]=useState(false);
  const [deleteConfirm,setDeleteConfirm]=useState(null); // cuenta a eliminar | null
  const [deleting,setDeleting]=useState(false);

  const showToast=(msg,type="success")=>{setToast({msg,type});setTimeout(()=>setToast({msg:"",type:""}),3500);};

  const inputSt={background:"rgba(255,255,255,0.06)",border:`1px solid ${C.border}`,
    borderRadius:8,padding:"9px 12px",color:"#f1f5f9",
    fontSize:"0.85rem",outline:"none",width:"100%",boxSizing:"border-box"};

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
        <Btn onClick={()=>{setAdding(a=>!a);setForm(EMPTY_CUENTA);}} variant={adding?"ghost":"primary"}>
          <i className={`fa-solid fa-${adding?"xmark":"plus"}`}/>{adding?"Cancelar":"Nueva cuenta"}
        </Btn>
      </div>

      {adding && (
        <div style={{background:C.bg1,border:`1px solid rgba(249,115,22,0.2)`,borderRadius:10,padding:18,marginBottom:16}}>
          <p style={{margin:"0 0 14px",fontWeight:600,color:C.text,fontSize:"0.85rem",display:"flex",alignItems:"center",gap:8}}>
            <i className="fa-solid fa-building-columns" style={{color:C.accent}}/>Nueva cuenta bancaria
          </p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))",gap:12,marginBottom:14}}>
            <div><Label required>Banco</Label>
              <input style={inputSt} placeholder="Ej: Banco Nacional" value={form.banco}
                onChange={e=>setForm(f=>({...f,banco:e.target.value}))}/></div>
            <div><Label required>N° Cuenta</Label>
              <input style={inputSt} placeholder="Número de cuenta" value={form.numero_cuenta}
                onChange={e=>setForm(f=>({...f,numero_cuenta:e.target.value}))}/></div>
            <div><Label>IBAN</Label>
              <input style={inputSt} placeholder="Ej: CR21015201001026284066" value={form.iban}
                maxLength={34} onChange={e=>setForm(f=>({...f,iban:e.target.value.toUpperCase()}))}/></div>
            <div><Label>Moneda</Label>
              <select style={inputSt} value={form.moneda} onChange={e=>{setForm(f=>({...f,moneda:e.target.value}));if(e.target.value==='CRC')setUsdBlockAlert(false);}}>
                {MONEDAS.map(m=><option key={m}>{m}</option>)}</select></div>
            <div><Label>Saldo inicial</Label>
              <input style={inputSt} type="number" value={form.saldo}
                onChange={e=>setForm(f=>({...f,saldo:e.target.value}))}/></div>
          </div>
          {usdBlockAlert && (
            <div style={{display:"flex",alignItems:"flex-start",gap:10,
              background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.28)",
              borderLeft:"3px solid #ef4444",borderRadius:8,padding:"10px 14px",marginBottom:12,
              color:"#fca5a5",fontSize:"0.83rem",lineHeight:1.55}}>
              <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",flexShrink:0,marginTop:2}}/>
              <span style={{flex:1}}>
                Para registrar cuentas en <strong style={{color:"#f87171"}}>{form.moneda}</strong>, primero debés activar{" "}
                <strong style={{color:"#f87171"}}>Soporte Multimoneda (USD)</strong> en la pestaña{" "}
                <em>Condominio</em>.
              </span>
              <button type="button" onClick={()=>setUsdBlockAlert(false)}
                style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",padding:0,flexShrink:0,fontSize:"0.85rem"}}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
          )}
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <Btn onClick={async()=>{
              if(!multimoneda && form.moneda!=='CRC'){setUsdBlockAlert(true);return;}
              setUsdBlockAlert(false);
              try{await create(form);setForm(EMPTY_CUENTA);setAdding(false);showToast("Cuenta creada");}catch(e){showToast(e.message,"error");}
            }}>
              <i className="fa-solid fa-floppy-disk"/>Guardar cuenta
            </Btn>
          </div>
        </div>
      )}

      {loading ? <div style={{textAlign:"center",padding:32,color:C.muted}}><i className="fa-solid fa-circle-notch fa-spin" style={{color:C.accent,marginRight:8}}/>Cargando…</div>
      : cuentas.length===0 ? <div style={{textAlign:"center",padding:40,color:C.muted}}>
          <i className="fa-solid fa-building-columns" style={{fontSize:"2rem",display:"block",marginBottom:10,opacity:0.2}}/>
          <p style={{margin:0,fontSize:"0.85rem"}}>No hay cuentas registradas.</p></div>
      : <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {cuentas.map(c=>{
            const mc=MONEDA_COLOR[c.moneda]??C.accent;
            return (
              <div key={c.id} style={{display:"flex",flexDirection:"column",gap:0}}>
              <div style={{
                background:C.bg1,
                border:`1px solid ${deleteConfirm?.id===c.id ? "rgba(239,68,68,0.4)" : C.border}`,
                borderRadius: deleteConfirm?.id===c.id ? "10px 10px 0 0" : 10,
                overflow:"hidden",
                transition:"border-color 0.15s",
              }}
                onMouseOver={e=>{ if(deleteConfirm?.id!==c.id) e.currentTarget.style.borderColor="rgba(249,115,22,0.2)"; }}
                onMouseOut={e=>{ if(deleteConfirm?.id!==c.id) e.currentTarget.style.borderColor=C.border; }}>
                {editId===c.id?(
                  <div style={{padding:16}}>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10,marginBottom:12}}>
                      <div><Label>Banco</Label><input style={inputSt} value={editForm.banco} onChange={e=>setEditForm(f=>({...f,banco:e.target.value}))}/></div>
                      <div><Label>N° Cuenta</Label><input style={inputSt} value={editForm.numero_cuenta} onChange={e=>setEditForm(f=>({...f,numero_cuenta:e.target.value}))}/></div>
                      <div><Label>IBAN</Label><input style={inputSt} value={editForm.iban||""} maxLength={34} placeholder="Ej: CR21..." onChange={e=>setEditForm(f=>({...f,iban:e.target.value.toUpperCase()}))}/></div>
                      <div><Label>Moneda</Label><select style={inputSt} value={editForm.moneda} onChange={e=>{setEditForm(f=>({...f,moneda:e.target.value}));if(e.target.value==='CRC')setUsdEditBlockAlert(false);}}>{MONEDAS.map(m=><option key={m}>{m}</option>)}</select></div>
                      <div><Label>Saldo</Label><input style={inputSt} type="number" value={editForm.saldo} onChange={e=>setEditForm(f=>({...f,saldo:e.target.value}))}/></div>
                    </div>
                    {usdEditBlockAlert && editId===c.id && (
                      <div style={{display:"flex",alignItems:"flex-start",gap:10,
                        background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.28)",
                        borderLeft:"3px solid #ef4444",borderRadius:8,padding:"10px 14px",marginBottom:12,
                        color:"#fca5a5",fontSize:"0.83rem",lineHeight:1.55}}>
                        <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",flexShrink:0,marginTop:2}}/>
                        <span style={{flex:1}}>
                          Para usar moneda <strong style={{color:"#f87171"}}>{editForm.moneda}</strong>, primero debés activar{" "}
                          <strong style={{color:"#f87171"}}>Soporte Multimoneda (USD)</strong> en la pestaña <em>Condominio</em>.
                        </span>
                        <button type="button" onClick={()=>setUsdEditBlockAlert(false)}
                          style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",padding:0,flexShrink:0,fontSize:"0.85rem"}}>
                          <i className="fa-solid fa-xmark"/>
                        </button>
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                      <Btn small variant="ghost" onClick={()=>{setEditId(null);setUsdEditBlockAlert(false);}}>Cancelar</Btn>
                      <Btn small onClick={async()=>{
                        if(!multimoneda && editForm.moneda!=='CRC'){setUsdEditBlockAlert(true);return;}
                        setUsdEditBlockAlert(false);
                        try{await update(c.id,editForm);setEditId(null);showToast("Cuenta actualizada");}catch(e){showToast(e.message,"error");}
                      }}>
                        <i className="fa-solid fa-floppy-disk"/>Guardar
                      </Btn>
                    </div>
                  </div>
                ):(
                  <div style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                    <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:`${mc}14`,border:`1px solid ${mc}28`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <i className="fa-solid fa-building-columns" style={{color:mc,fontSize:"1rem"}}/>
                    </div>
                    <div style={{flex:1,minWidth:160}}>
                      <div style={{fontWeight:700,color:C.text,fontSize:"0.9rem"}}>{c.banco}</div>
                      <div style={{color:C.muted,fontSize:"0.78rem",fontFamily:"monospace",marginTop:2,display:"flex",flexWrap:"wrap",alignItems:"center",gap:6}}>
                        {c.numero_cuenta}
                        {c.iban&&<span style={{color:"#94a3b8",fontSize:"0.72rem",fontFamily:"monospace",display:"inline-flex",alignItems:"center",gap:4}}>
                          <i className="fa-solid fa-landmark" style={{fontSize:"0.6rem"}}/>IBAN: {c.iban}</span>}
                        <span style={{padding:"1px 7px",borderRadius:4,background:`${mc}18`,color:mc,fontSize:"0.68rem",fontWeight:700}}>{c.moneda}</span>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:"auto"}}>
                      <div style={{textAlign:"right",marginRight:4}}>
                        <div style={{fontWeight:700,color:C.text,fontSize:"0.92rem",fontFamily:"monospace"}}>{MONEDA_SYMBOL[c.moneda]??c.moneda}{formatCurrency(Number(c.saldo))}</div>
                        <div style={{fontSize:"0.68rem",color:C.muted,marginTop:1}}>saldo actual</div>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        <Btn small variant="ghost" onClick={()=>{setEditId(c.id);setEditForm({banco:c.banco,numero_cuenta:c.numero_cuenta,iban:c.iban||"",moneda:c.moneda,saldo:c.saldo});}}>
                          <i className="fa-solid fa-pencil"/>
                        </Btn>
                        <Btn small variant="danger" onClick={()=>{ setDeleteConfirm(c); setEditId(null); }}>
                          <i className="fa-solid fa-trash"/>
                        </Btn>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Panel de confirmación de borrado ── */}
              {deleteConfirm?.id===c.id && (
                <div style={{
                  background:"rgba(239,68,68,0.07)",
                  border:"1px solid rgba(239,68,68,0.35)",
                  borderTop:"none",
                  borderRadius:"0 0 10px 10px",
                  padding:"14px 18px",
                }}>
                  <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:12}}>
                    <i className="fa-solid fa-triangle-exclamation"
                       style={{color:"#f87171",fontSize:"1rem",marginTop:2,flexShrink:0}}/>
                    <div style={{fontSize:"0.84rem",color:C.text,lineHeight:1.55}}>
                      <strong style={{color:"#f87171"}}>¿Eliminar la cuenta "{c.banco}"?</strong><br/>
                      <span style={{color:C.muted,fontSize:"0.79rem"}}>
                        Esta acción no se puede deshacer. Si tiene movimientos o recibos vinculados,
                        la cuenta puede estar bloqueada.
                      </span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button type="button"
                      onClick={()=>setDeleteConfirm(null)}
                      disabled={deleting}
                      style={{padding:"7px 16px",borderRadius:7,fontSize:"0.82rem",fontWeight:600,
                        cursor:"pointer",border:`1px solid ${C.border}`,
                        background:"transparent",color:C.muted,opacity:deleting?0.5:1}}>
                      Cancelar
                    </button>
                    <button type="button"
                      disabled={deleting}
                      onClick={async()=>{
                        setDeleting(true);
                        try{
                          await remove(c.id);
                          setDeleteConfirm(null);
                          showToast("Cuenta eliminada");
                        }catch(e){
                          showToast(e.message,"error");
                        }finally{
                          setDeleting(false);
                        }
                      }}
                      style={{padding:"7px 18px",borderRadius:7,fontSize:"0.82rem",fontWeight:700,
                        cursor:deleting?"not-allowed":"pointer",
                        border:"1px solid rgba(239,68,68,0.4)",
                        background:"rgba(239,68,68,0.15)",color:"#f87171",
                        display:"inline-flex",alignItems:"center",gap:7,
                        opacity:deleting?0.6:1}}>
                      {deleting
                        ? <><i className="fa-solid fa-circle-notch fa-spin"/>Eliminando…</>
                        : <><i className="fa-solid fa-trash"/>Sí, eliminar</>}
                    </button>
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>}
    </div>
  );
}

function EmailSection() {
  const { data, loading, update } = useCondominio();
  const [form,setForm]=useState(null);
  const [saving,setSaving]=useState(false);
  const [toast,setToast]=useState({msg:"",type:""});
  const [showPw,setShowPw]=useState(false);

  if (data&&!form) setForm({
    email_host_user:     data.email_host_user     ??"",
    email_host_password: data.email_host_password ??"",
    email_host:          data.email_host          ||"smtp.gmail.com",
    email_port:          data.email_port          ||465,
  });

  const field=key=>({value:form?.[key]??"",onChange:e=>setForm(f=>({...f,[key]:e.target.value}))});

  async function handleSave() {
    setSaving(true);setToast({msg:"",type:""});
    try {
      const fd=new FormData();
      fd.append("email_host_user",form.email_host_user);
      fd.append("email_host_password",form.email_host_password);
      fd.append("email_host",form.email_host||"smtp.gmail.com");
      fd.append("email_port",form.email_port||465);
      if(data){fd.append("nombre",data.nombre??"");fd.append("cedulaJuridica",data.cedulaJuridica??"");fd.append("email",data.email??"");}
      await update(fd);
      setToast({msg:"Configuración de correo guardada",type:"success"});
    } catch(e){setToast({msg:e.message,type:"error"});}
    finally{setSaving(false);}
  }

  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted}}><i className="fa-solid fa-circle-notch fa-spin" style={{color:C.accent,marginRight:8}}/>Cargando…</div>;
  const hasConfig=!!(data?.email_host_user&&data?.email_host_password);

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type}/>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,
        background:hasConfig?"rgba(16,185,129,0.06)":"rgba(249,115,22,0.06)",
        border:`1px solid ${hasConfig?"rgba(16,185,129,0.2)":"rgba(249,115,22,0.2)"}`,
        borderRadius:10,padding:"12px 16px"}}>
        <i className={`fa-solid ${hasConfig?"fa-circle-check":"fa-circle-exclamation"}`}
           style={{color:hasConfig?"#10b981":"#f97316",fontSize:"0.9rem",flexShrink:0}}/>
        <div>
          <div style={{fontWeight:600,color:C.text,fontSize:"0.84rem"}}>{hasConfig?"Correo configurado":"Correo no configurado"}</div>
          <div style={{color:C.muted,fontSize:"0.75rem",marginTop:2}}>
            {hasConfig?`Envíos desde: ${data.email_host_user}`:"Configure el correo del condominio"}
          </div>
        </div>
      </div>
      <div style={{background:"rgba(96,165,250,0.06)",border:"1px solid rgba(96,165,250,0.15)",borderRadius:10,padding:"14px 16px",marginBottom:20}}>
        <p style={{margin:"0 0 4px",fontWeight:600,color:"#93c5fd",fontSize:"0.82rem",display:"flex",alignItems:"center",gap:8}}>
          <i className="fa-brands fa-google" style={{fontSize:"0.78rem"}}/>Gmail — use Contraseña de Aplicación
        </p>
        <p style={{margin:0,color:C.muted,fontSize:"0.76rem",lineHeight:1.6}}>
          Vaya a <em>Seguridad → Verificación en 2 pasos → Contraseñas de aplicación</em>.
          Servidor: <code style={{background:"rgba(0,0,0,0.3)",padding:"1px 5px",borderRadius:4}}>smtp.gmail.com</code> puerto <code style={{background:"rgba(0,0,0,0.3)",padding:"1px 5px",borderRadius:4}}>465</code>.
        </p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))",gap:14,marginBottom:20}}>
        <div style={{gridColumn:"1 / -1"}}><Label required>Correo remitente</Label>
          <Input {...field("email_host_user")} type="email" placeholder="admin@condominio.com"/></div>
        <div style={{gridColumn:"1 / -1",position:"relative"}}><Label required>Contraseña de aplicación</Label>
          <div style={{position:"relative"}}>
            <Input {...field("email_host_password")} type={showPw?"text":"password"} placeholder="xxxx xxxx xxxx xxxx" style={{paddingRight:44}}/>
            <button type="button" onClick={()=>setShowPw(v=>!v)}
              style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,cursor:"pointer",padding:0,fontSize:"0.85rem"}}>
              <i className={`fa-solid fa-eye${showPw?"-slash":""}`}/>
            </button>
          </div>
        </div>
        <div><Label>Servidor SMTP</Label><Input {...field("email_host")} placeholder="smtp.gmail.com"/></div>
        <div><Label>Puerto SMTP</Label><Input {...field("email_port")} type="number" placeholder="465"/></div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",paddingTop:16,borderTop:`1px solid ${C.border}`}}>
        <Btn onClick={handleSave} disabled={saving}>
          {saving?<><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</>:<><i className="fa-solid fa-floppy-disk"/>Guardar configuración</>}
        </Btn>
      </div>
    </div>
  );
}

const TABS=[
  {key:"condominio",label:"Condominio",       icon:"fa-building"},
  {key:"cuentas",   label:"Cuentas Bancarias",icon:"fa-building-columns"},
  {key:"correo",    label:"Correo",            icon:"fa-envelope"},
];

export default function SettingsPage() {
  const [tab,setTab]=useState("condominio");
  const subtitles={
    condominio:"Información general y branding del condominio",
    cuentas:"Cuentas bancarias registradas",
    correo:"Configuración del correo para envío de facturas y recibos",
  };
  return (
    <div>
      <div style={{marginBottom:28}}>
        <span style={{color:"#9ca3af"}}>
          <Link to="/"><span style={{color:"#9ca3af"}}>Inicio </span></Link>/ <span>Configuración</span>
        </span>
        <p className="mt-3 mb-3" style={{color:"#9ca3af"}}>Administra la información y configuración de tu condominio</p>
      </div>
      <div style={{display:"flex",gap:3,marginBottom:20,background:C.bg2,borderRadius:12,padding:4,border:`1px solid ${C.border}`}}>
        {TABS.map(t=>{
          const active=tab===t.key;
          return (
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{flex:1,padding:"10px 16px",borderRadius:9,
                background:active?C.bg3:"transparent",
                border:active?`1px solid ${C.border}`:"1px solid transparent",
                color:active?C.accent:C.muted,fontWeight:active?700:400,fontSize:"0.85rem",
                cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                gap:8,transition:"all 0.15s",fontFamily:"'Inter',sans-serif"}}>
              <i className={`fa-solid ${t.icon}`} style={{fontSize:"0.8rem"}}/>{t.label}
            </button>
          );
        })}
      </div>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden"}}>
        <div style={{padding:"16px 22px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:12,background:C.bg1}}>
          <div style={{width:34,height:34,borderRadius:8,background:"rgba(249,115,22,0.1)",border:"1px solid rgba(249,115,22,0.2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <i className={`fa-solid ${TABS.find(t=>t.key===tab)?.icon}`} style={{color:C.accent,fontSize:"0.85rem"}}/>
          </div>
          <div>
            <h3 style={{margin:0,fontWeight:700,color:C.text,fontSize:"0.95rem",fontFamily:"'Manrope',sans-serif"}}>
              {TABS.find(t=>t.key===tab)?.label}
            </h3>
            <p style={{margin:0,color:C.muted,fontSize:"0.73rem"}}>{subtitles[tab]}</p>
          </div>
        </div>
        <div style={{padding:"22px 24px"}}>
          {tab==="condominio"&&<CondominioSection/>}
          {tab==="cuentas"   &&<CuentasSection/>}
          {tab==="correo"    &&<EmailSection/>}
        </div>
      </div>
    </div>
  );
}
